/* 從解析後的文件提煉知識候選。
   完全在本機、不呼叫任何模型（這是訪談時的決定：成人內容永不出裝置，分類只是「建議」）。

   兩句摘要的紀律在這裡也適用：一個章節＝一條候選，摘要取該章節前兩句（≤120 字），其餘進正文。
   沒有標題的文件（純文字、多數 PDF）改用長度分組。 */
import type { Block, ParsedDoc } from "./parsers.ts";
import { splitSentences, SUMMARY_MAX_CHARS, deriveTitle } from "../domain/rules.ts";
import { bigrams } from "../domain/rules.ts";
import type { Category, Concept } from "../domain/types.ts";

export interface ExtractedConcept {
  title: string;
  summary: string;
  body: string;
  /** 來源定位：讓人回頭找得到它出自哪一份文件的哪一段。 */
  sourceRef: string;
  headingPath: string[];
}

export interface ExtractOptions {
  /** 一份文件最多提煉幾條，避免 200 頁 PDF 一次炸出上千條。 */
  max?: number;
  /** 太短的段落不值得成為一條知識。 */
  minChars?: number;
}

const DEFAULT_MAX = 60;
/* 20 字 ≈ 一個完整的短句。設 40 會把「粒線體是細胞的能量工廠，擁有自己獨立的 DNA。」這種
   正當的單句概念整個丟掉 —— 知識原子本來就可以很短。（原本設 40 是測試逼出來的修正。） */
const DEFAULT_MIN_CHARS = 20;

export function summarize(text: string, maxChars = SUMMARY_MAX_CHARS, maxSentences = 2): string {
  /* 用 lookbehind 在句末標點「之後」切，標點才會留在句子裡。
     用 String.split 會把標點吃掉，摘要就變成沒有句號的一串字。 */
  const sentences = splitSentences(text).length
    ? text.trim().split(/(?<=[。！？!?])/).map((s) => s.trim()).filter(Boolean)
    : [];
  if (!sentences.length) return text.slice(0, maxChars);
  let out = "";
  for (const s of sentences.slice(0, maxSentences)) {
    const next = out ? out + s : s;
    if (next.length > maxChars) break;
    out = next;
  }
  if (!out) out = sentences[0]!.slice(0, maxChars);
  return out;
}

interface Section { headings: string[]; blocks: Block[] }

function toSections(blocks: Block[]): Section[] {
  const sections: Section[] = [];
  const stack: string[] = [];
  let current: Section | null = null;
  for (const block of blocks) {
    if (block.kind === "heading") {
      stack.length = Math.max(0, block.level - 1);
      stack[block.level - 1] = block.text;
      current = { headings: stack.filter(Boolean).slice(), blocks: [] };
      sections.push(current);
      continue;
    }
    if (!current) { current = { headings: [], blocks: [] }; sections.push(current); }
    current.blocks.push(block);
  }
  return sections;
}

/** 依長度分組。只吃段落，不吃標題 —— 標題已經被當成分組依據時，再塞進內文會變成重複。 */
function groupByLength(blocks: Block[], target = 600): Block[][] {
  const groups: Block[][] = [];
  let bucket: Block[] = [];
  let size = 0;
  for (const block of blocks) {
    if (block.kind === "heading") continue;
    bucket.push(block);
    size += block.text.length;
    if (size >= target) { groups.push(bucket); bucket = []; size = 0; }
  }
  if (bucket.length) groups.push(bucket);
  return groups;
}

export function extractConcepts(doc: ParsedDoc, opts: ExtractOptions = {}): ExtractedConcept[] {
  const max = opts.max ?? DEFAULT_MAX;
  const minChars = opts.minChars ?? DEFAULT_MIN_CHARS;
  const out: ExtractedConcept[] = [];
  const hasHeadings = doc.blocks.some((b) => b.kind === "heading");

  const push = (headings: string[], text: string, sourceRef: string) => {
    const clean = text.replace(/\s+/g, " ").trim();
    if (clean.length < minChars) return;
    const summary = summarize(clean);
    const rest = clean.slice(summary.length).trim();
    out.push({
      title: headings.length ? headings[headings.length - 1]!.slice(0, 24) : deriveTitle(summary),
      summary, body: rest, sourceRef, headingPath: headings,
    });
  };

  if (hasHeadings) {
    const sections = toSections(doc.blocks);
    sections.forEach((section, index) => {
      const text = section.blocks.map((b) => b.text).join(" ");
      if (!text.trim()) return;
      const ref = section.headings.length
        ? doc.title + " › " + section.headings.join(" › ")
        : doc.title + " 第 " + (index + 1) + " 段";
      push(section.headings, text, ref);
    });
    /* 只有「單一章節但內容很大」才需要改用長度分組（代表標題沒有真的把內容切開）。
       單一章節且內容不大時，它就是一條知識 —— 不該硬拆，也不該把標題重複塞進摘要。 */
    const onlyText = doc.blocks.filter((b) => b.kind === "paragraph").map((b) => b.text).join(" ");
    if (out.length <= 1 && onlyText.length > 3000) {
      out.length = 0;
      groupByLength(doc.blocks).forEach((group, index) => {
        push([], group.map((b) => b.text).join(" "), doc.title + " 第 " + (index + 1) + " 段");
      });
    }
  } else {
    groupByLength(doc.blocks).forEach((group, index) => {
      push([], group.map((b) => b.text).join(" "), doc.title + " 第 " + (index + 1) + " 段");
    });
  }

  /* 同一個摘要只留一條（文件裡常有重複的頁首頁尾）。 */
  const seen = new Set<string>();
  const unique = out.filter((c) => {
    const key = c.summary.slice(0, 40);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return unique.slice(0, max);
}

export interface CategorySuggestion {
  categoryId: string;
  score: number;
  /** 0–1，只是相對強度，不是機率 —— 介面上要說「建議」，不能說「判斷」。 */
  confidence: number;
  because: string;
}

/** 分類建議：用**你自己的知識庫**當訓練資料 —— 零下載、完全私密。 */
export function suggestCategory(
  text: string, categories: Category[], concepts: Concept[], adultUnlocked: boolean,
): CategorySuggestion | null {
  const grams = new Set(bigrams(text));
  if (!grams.size) return null;
  const scored = categories.map((category) => {
    const corpus = [
      category.name, category.name, category.definition,
      ...concepts
        .filter((c) => c.primaryCategoryId === category.id && (adultUnlocked || !c.nsfw) && c.status !== "trashed")
        .slice(0, 200)
        .map((c) => c.summary + " " + c.note),
    ].join(" ");
    const corpusGrams = new Set(bigrams(corpus));
    let hits = 0;
    for (const g of grams) if (corpusGrams.has(g)) hits++;
    const nameHit = category.name.length >= 2 && text.includes(category.name) ? 3 : 0;
    return { categoryId: category.id, score: hits + nameHit, category };
  }).filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score);

  if (!scored.length) return null;
  const best = scored[0]!;
  const second = scored[1]?.score ?? 0;
  const confidence = Math.min(1, (best.score - second) / Math.max(4, best.score) + best.score / 40);
  return {
    categoryId: best.categoryId, score: best.score, confidence: Math.round(confidence * 100) / 100,
    because: best.score >= 6 ? "與這個類別的既有知識大量重疊" : "關鍵詞與這個類別相符",
  };
}
